import * as Tooltip from "@radix-ui/react-tooltip";
import classNames from "classnames";
import React from "react";
import KuikaSmallText from "../typography/SmallText/SmallText";
import { KUIKA_TOOLTIP_DELAY_MS, useHasKuikaTooltipProvider } from "./kuika-tooltip-provider";
import "./kuika-tooltip.scss";
import { isPopoverFocusRestore } from "./tooltip-focus-guard";

type KuikaTooltipPosition = "top" | "right" | "bottom" | "left";
// `inverse`: the canvas selection overlay's surface (--selection-bar-*), themed light or dark with the designer.
type KuikaTooltipBgColor = "green" | "red" | "yellow" | "none" | "inverse";
type KuikaTooltipAlign = "start" | "center" | "end";

interface KuikaTooltipProps {
  children: React.ReactNode;
  content: React.ReactNode | null | undefined;
  position?: KuikaTooltipPosition;
  delayDuration?: number;
  bgcolor?: KuikaTooltipBgColor | null;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  sideOffset?: number;
  align?: KuikaTooltipAlign;
  contentClassName?: string;
  /**
   * Close on the frame the pointer leaves, with no exit animation. For a tooltip whose anchor is a
   * control its row only shows while hovered: the anchor moves or leaves the layout in that same
   * frame, and an exit that outlives it is dragged along with it.
   */
  instantExit?: boolean;
  /**
   * Test id for the floating content. The tooltip is what explains a blocked affordance, so the
   * copy is assertable state — and `role="tooltip"` alone cannot say WHICH tooltip is open.
   */
  contentTestId?: string;
  defaultOpen?: boolean;
  /**
   * Keep the Radix tree mounted while `content` is empty. Turn it on when `content`
   * comes and goes: the bare-child fallback changes the element type, so React
   * unmounts everything below it — tearing a child down mid-interaction.
   */
  keepMounted?: boolean;
}

interface KuikaTooltipArrowProps {
  bgcolor: KuikaTooltipBgColor | null;
  position: KuikaTooltipPosition;
}

interface KuikaTooltipTriggerProps {
  "data-has-kuika-tooltip"?: string;
}

type TriggerElement = React.ReactElement<KuikaTooltipTriggerProps & React.ClassAttributes<HTMLElement>>;

/** The reserved `ref` field React attaches to an element, which the public ReactElement type never declares. */
interface ElementWithRef {
  ref?: React.Ref<HTMLElement> | null;
}

/** Reads the ref a trigger element already carries, if any, so it isn't dropped when one is merged in. */
const readExistingRef = (element: React.ReactElement): React.Ref<HTMLElement> | null | undefined =>
  (element as unknown as ElementWithRef).ref;

/**
 * Calls every ref in order with the same node, so a DOM element can carry more than one
 * ref at once — here, the ref forwarded in from outside KuikaTooltip (e.g. a Radix
 * `Popover.Trigger asChild` anchoring to it) alongside whatever ref the trigger child
 * already had.
 */
const mergeRefs = <T,>(...refs: (React.Ref<T> | undefined | null)[]): React.RefCallback<T> => {
  return (node: T | null): void => {
    refs.forEach((ref: React.Ref<T> | undefined | null): void => {
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        (ref as React.MutableRefObject<T | null>).current = node;
      }
    });
  };
};

const renderTooltipArrow: (props: KuikaTooltipArrowProps) => JSX.Element | null = ({
  bgcolor,
  position
}: KuikaTooltipArrowProps): JSX.Element | null => {
  if (bgcolor === "none") {
    return null;
  }

  const arrowClasses: string = classNames(
    "kuika-tooltip__arrow",
    bgcolor ? `kuika-tooltip__arrow--${bgcolor}` : null,
    `kuika-tooltip__arrow--${position}`
  );

  return (
    <Tooltip.Arrow asChild className={arrowClasses}>
      <svg aria-hidden="true" focusable="false" width="10" height="5" viewBox="0 0 30 10" preserveAspectRatio="none">
        <polygon className="kuika-tooltip__arrow-fill" points="0,0 30,0 15,10" />
        <path className="kuika-tooltip__arrow-border" d="M0 0L15 10L30 0" />
      </svg>
    </Tooltip.Arrow>
  );
};

/**
 * Keeps Radix from opening this tooltip on a focus that is not the user arriving at its trigger:
 * - focus on a nested trigger with a tooltip of its own (React's `onFocus` bubbles, so a tooltip around
 *   a field also heard its inner "Bind to data" button being focused, and both tooltips opened);
 * - focus a closing popover hands back to its trigger (see `tooltip-focus-guard.ts`).
 * Radix skips its open handler when the event is default-prevented.
 */
const handleTriggerFocus = (event: React.FocusEvent<HTMLElement>): void => {
  const owner: Element | null = (event.target as Element).closest("[data-has-kuika-tooltip]");
  const isNestedTrigger: boolean =
    owner !== null && owner !== event.currentTarget && event.currentTarget.contains(owner);
  if (isNestedTrigger || isPopoverFocusRestore()) {
    event.preventDefault();
  }
};

const KuikaTooltip = React.forwardRef<HTMLElement, KuikaTooltipProps>(
  (
    {
      children,
      content,
      position = "top",
      delayDuration = KUIKA_TOOLTIP_DELAY_MS,
      bgcolor = null,
      open,
      onOpenChange,
      sideOffset = 1,
      align = "center",
      contentClassName,
      instantExit,
      contentTestId,
      defaultOpen,
      keepMounted = false
    }: KuikaTooltipProps,
    forwardedRef: React.ForwardedRef<HTMLElement>
  ): JSX.Element => {
    const hasRootProvider: boolean = useHasKuikaTooltipProvider();

    if (!content && !keepMounted) {
      // A ref forwarded from outside (e.g. an ancestor `Popover.Trigger asChild`) still
      // needs to reach the real element even when this tooltip renders no content.
      if (!forwardedRef || !React.isValidElement(children)) {
        return <>{children}</>;
      }
      const bareChild: TriggerElement = children as TriggerElement;
      return (
        <>{React.cloneElement(bareChild, { ref: mergeRefs<HTMLElement>(forwardedRef, readExistingRef(bareChild)) })}</>
      );
    }

    // Mark the trigger so GlobalOnboardingTooltipLayer can skip attaching duplicate
    // hover/click listeners to elements that already have a KuikaTooltip wrapper, and
    // merge in a ref forwarded from outside so an `asChild` ancestor (e.g. a Radix
    // Popover.Trigger) can anchor to the same DOM node Tooltip.Trigger positions against.
    const markedTrigger: React.ReactNode = React.isValidElement(children)
      ? React.cloneElement(children as TriggerElement, {
          "data-has-kuika-tooltip": "true",
          ...(forwardedRef
            ? { ref: mergeRefs<HTMLElement>(forwardedRef, readExistingRef(children as TriggerElement)) }
            : {})
        })
      : children;
    const contentClasses: string = classNames(
      "kuika-tooltip__content",
      bgcolor ? `kuika-tooltip__content--${bgcolor}` : null,
      { "kuika-tooltip__content--instant-exit": instantExit },
      contentClassName
    );

    // The delay rides on the Root, so it holds under the shared provider as well as on a local one.
    const root: JSX.Element = (
      <Tooltip.Root open={open} onOpenChange={onOpenChange} defaultOpen={defaultOpen} delayDuration={delayDuration}>
        <Tooltip.Trigger asChild onFocus={handleTriggerFocus}>
          {markedTrigger}
        </Tooltip.Trigger>
        {content && (
          <Tooltip.Portal>
            <Tooltip.Content
              className={contentClasses}
              sideOffset={sideOffset}
              side={position}
              align={align}
              data-testid={contentTestId}
            >
              <KuikaSmallText>{content}</KuikaSmallText>
              {renderTooltipArrow({ bgcolor, position })}
            </Tooltip.Content>
          </Tooltip.Portal>
        )}
      </Tooltip.Root>
    );

    // Under KuikaTooltipProvider the tooltip joins the app's shared warm-up; a provider of its own would
    // hide that one from it. Outside it (tests, detached renders) Radix still needs a provider here.
    if (hasRootProvider) {
      return root;
    }

    return <Tooltip.Provider delayDuration={delayDuration}>{root}</Tooltip.Provider>;
  }
);

KuikaTooltip.displayName = "KuikaTooltip";

export default KuikaTooltip;
